/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useMemo, useRef, useState } from "react";
import styled from "styled-components";
import Label from "../Label";
import { useController } from "react-hook-form";
import { format, getDaysInMonth, setMonth, setYear, getMonth, getYear, getDate, setDate } from "date-fns";
import Button from "../Button";
import Icon from "../Icon";
import CalendarDatePicker from "./CalendarDatePicker";
import { generateYearsRange, getAvailableDaysInMonth } from "../../../utils/helpers/date";
import { MONTHS } from "../../../constants/constants";
import { StyledFormField, StyledSelectIcon, StyledSelectWrapper } from "./StyledFormField";
import { useTranslate } from "../../../hooks/useTranslate";

const DateFieldsWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;

  ${({ theme }) => theme.containerMedia.sm} {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr auto;
  }
`;

const DateFieldSelect = styled(StyledSelectWrapper)`
  max-width: 160px;

  ${({ theme }) => theme.containerMedia.sm} {
    max-width: 100%;
    grid-column: span 4;
  }
`;

const YearSelect = styled(StyledSelectWrapper)`
  ${({ theme }) => theme.containerMedia.sm} {
    grid-column: span 3;
  }
`;

const CalendarButtonWrapper = styled.div`
  position: relative;

  ${({ theme }) => theme.containerMedia.sm} {
    justify-self: end;
  }
`;

const DateField = ({
  name,
  control,
  label,
  required = false,
  defaultValue,
  validationRules = {},
  ...props
}) => {
  const { _t } = useTranslate();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState("bottom");
  const calendarButtonRef = useRef(null);
  const {
    field: { value, onChange },
    fieldState,
  } = useController({
    name,
    control,
    rules: {
      required: required ? "error_value_is_empty" : undefined,
      ...validationRules,
    },
    defaultValue: defaultValue || "",
  });

  const [dateValues, setDateValues] = useState({
    day: value ? getDate(value) : "",
    month: value ? getMonth(value) + 1 : "",
    year: value ? getYear(value) : "",
  });

  const years = useMemo(() => generateYearsRange(), []);
  const daysInMonth = useMemo(
    () => getAvailableDaysInMonth(new Date(dateValues.year, dateValues.month - 1, dateValues.day)),
    [dateValues.day, dateValues.month, dateValues.year]
  );

  const handleDateChange = (newValues) => {
    setDateValues(newValues);

    if (newValues.day && newValues.month && newValues.year) {
      let date = new Date();
      date = setYear(date, newValues.year);
      date = setMonth(date, newValues.month - 1);
      date = setDate(date, newValues.day);

      const dateString = format(date, "yyyy-MM-dd");
      onChange(dateString);
    }
  };

  const handleSelectChange = (type) => (event) => {
    const newValue = parseInt(event.target.value) || "";
    let newValues = { ...dateValues, [type]: newValue };

    if ((type === "month" || type === "year") && dateValues.day) {
      let date = new Date();
      date = setYear(date, type === "year" ? newValue : dateValues.year);
      date = setMonth(date, (type === "month" ? newValue : dateValues.month) - 1);

      const maxDays = getDaysInMonth(date);

      if (dateValues.day > maxDays) {
        newValues.day = maxDays;
      }
    }

    handleDateChange(newValues);
  };

  const handleDatePickerChange = (date) => {
    if (!date) return;

    const newValues = {
      day: getDate(date),
      month: getMonth(date) + 1,
      year: getYear(date),
    };

    handleDateChange(newValues);
    setIsCalendarOpen(false);
  };

  return (
    <>
      {label && (
        <Label name={name} required={required}>
          {label}
        </Label>
      )}

      <DateFieldsWrapper>
        <DateFieldSelect>
          <StyledFormField.Select
            value={dateValues.day}
            onChange={handleSelectChange("day")}
            aria-label={_t("Day")}
            aria-invalid={!!fieldState.error}
            aria-describedby={fieldState.error ? `${name}-error` : undefined}
            {...props}
            $error={fieldState.error}
          >
            <option value="" disabled>
              {_t("Day")}
            </option>
            {daysInMonth.map((day) => (
              <option key={day} value={day}>
                {day}
              </option>
            ))}
          </StyledFormField.Select>
          <StyledSelectIcon />
        </DateFieldSelect>

        <DateFieldSelect>
          <StyledFormField.Select
            value={dateValues.month}
            onChange={handleSelectChange("month")}
            aria-label={_t("Month")}
            aria-invalid={!!fieldState.error}
            aria-describedby={fieldState.error ? `${name}-error` : undefined}
            $error={fieldState.error}
            {...props}
          >
            <option value="" disabled>
              {_t("Month")}
            </option>
            {MONTHS.map((month) => (
              <option key={month.value} value={month.value}>
                {_t(month.label)}
              </option>
            ))}
          </StyledFormField.Select>
          <StyledSelectIcon />
        </DateFieldSelect>

        <YearSelect>
          <StyledFormField.Select
            value={dateValues.year}
            onChange={handleSelectChange("year")}
            aria-label={_t("Year")}
            aria-invalid={!!fieldState.error}
            aria-describedby={fieldState.error ? `${name}-error` : undefined}
            $error={fieldState.error}
            {...props}
          >
            <option value="" disabled>
              {_t("Year")}
            </option>
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </StyledFormField.Select>
          <StyledSelectIcon />
        </YearSelect>

        <CalendarButtonWrapper>
          <Button
            ref={calendarButtonRef}
            variant="transparent"
            type="button"
            size="icon"
            onClick={() => {
              if (!isCalendarOpen && calendarButtonRef.current) {
                const triggerRect = calendarButtonRef.current.getBoundingClientRect();
                const spaceBelow = window.innerHeight - triggerRect.bottom;
                setCalendarPosition(spaceBelow < 300 && triggerRect.top > spaceBelow ? "top" : "bottom");
              }
              setIsCalendarOpen(!isCalendarOpen);
            }}
            icon={<Icon.CalendarDetailed color="dark" />}
            aria-label={_t("Open calendar")}
            disabled={props.disabled}
          />
          <CalendarDatePicker
            show={isCalendarOpen}
            position={calendarPosition}
            onClose={() => setIsCalendarOpen(false)}
            triggerRef={calendarButtonRef}
            selectedDate={dateValues.year ? format(new Date(dateValues.year, dateValues.month - 1, dateValues.day), "yyyy-MM-dd") : null}
            onChange={handleDatePickerChange}
          />
        </CalendarButtonWrapper>
      </DateFieldsWrapper>
    </>
  );
};

export default DateField;
